/**
 * Save-score + leaderboard overlay.
 *
 * Same flat-color visual language as GameOverOverlay / LevelCompleteOverlay:
 * sky-blue panel, pink-star title, navy cards for the score / leaderboard.
 *
 * Usage:
 *   const overlay = new ScoreSubmitOverlay();
 *   overlay.show({
 *       game: 'frogger',
 *       score: 1280,
 *       outcome: 'win',
 *       meta: { time: '1:42', rows: 60 },
 *       theme: gameInstance.theme,
 *       onDone: () => { ... },
 *   });
 */

import { ScoreService } from '../services/ScoreService.js';
import { t } from '../config/i18n.js';
import { injectOverlayVars } from './overlayPalette.js';

let _cssInjected = false;
function injectCss() {
    if (_cssInjected) return;
    _cssInjected = true;
    injectOverlayVars();
    const css = `
    .ss-root { position:fixed; inset:0; z-index:9200; display:flex; align-items:center; justify-content:center;
      background:var(--ov-backdrop); font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif; padding:24px; }
    .ss-panel { position:relative; background:var(--ov-panel-bg);
      border:var(--ov-panel-border-width) solid var(--ov-panel-border);
      /* Sharp corners — modal frame is square per UX direction. */
      border-radius:0; padding:32px 38px 28px; width:100%; max-width:520px; max-height:90vh;
      box-shadow:0 24px 60px rgba(0,0,0,0.45); display:flex; flex-direction:column; gap:18px; overflow:auto; }
    .ss-title-row { display:flex; align-items:center; justify-content:center; gap:18px; }
    .ss-star { color:var(--ov-star); font-size:30px; line-height:1;
      filter:drop-shadow(0 2px 0 rgba(0,0,0,0.18)); }
    .ss-title { font-size:30px; font-weight:900; color:var(--ov-title);
      letter-spacing:1.2px; text-shadow:0 3px 0 var(--ov-title-shadow); text-align:center; }

    .ss-card { background:var(--ov-card-bg);
      border-radius:0; padding:16px 22px; }
    .ss-score-card { text-align:center; }
    .ss-score-label { color:var(--ov-label); font-size:14px; font-weight:700;
      letter-spacing:1.6px; margin-bottom:4px; }
    .ss-score-val { color:var(--ov-accent-score); font-size:54px; font-weight:900;
      text-shadow:0 3px 0 var(--ov-value-shadow); line-height:1.1; }

    .ss-form { display:flex; flex-direction:column; gap:12px; }
    .ss-input { width:100%; background:var(--ov-input-bg); border:3px solid var(--ov-input-edge);
      border-radius:0; padding:14px 20px; font-size:18px; font-weight:700;
      color:#ffffff; outline:none; font-family:inherit; transition:border-color 0.15s ease, box-shadow 0.15s ease; }
    .ss-input::placeholder { color:#7a9bc1; }
    .ss-input:focus { border-color:var(--ov-input-focus); box-shadow:0 0 0 4px rgba(246,201,72,0.22); }

    .ss-btn-row { display:flex; gap:10px; }
    .ss-btn { border:none; cursor:pointer; font-family:inherit; font-weight:900; letter-spacing:0.6px;
      padding:14px 22px; border-radius:0; font-size:18px;
      display:flex; align-items:center; justify-content:center; gap:8px;
      transition:transform 0.12s ease, filter 0.12s ease;
      box-shadow:0 5px 0 rgba(0,0,0,0.18); }
    .ss-btn:hover { transform:translateY(-2px); filter:brightness(1.06); }
    .ss-btn:active { transform:translateY(1px); box-shadow:0 2px 0 rgba(0,0,0,0.18); }
    .ss-btn:disabled { opacity:0.55; cursor:default; transform:none; }
    .ss-btn-save { flex:1; background:var(--ov-btn-retry-bg); color:var(--ov-btn-retry-fg);
      border-bottom:4px solid var(--ov-btn-retry-edge); }
    .ss-btn-skip { background:var(--ov-btn-home-bg); color:var(--ov-btn-home-fg);
      border-bottom:4px solid var(--ov-btn-home-edge); padding-left:24px; padding-right:24px; }
    .ss-btn-icon { width:22px; height:22px; }
    .ss-btn-icon svg { width:100%; height:100%; }

    .ss-status { min-height:20px; text-align:center; font-size:14px; font-weight:700; letter-spacing:0.4px; }
    .ss-status-ok { color:var(--ov-ok); }
    .ss-status-err { color:var(--ov-err); }

    .ss-lb-title { text-align:center; color:var(--ov-label); font-size:14px; font-weight:700;
      letter-spacing:1.8px; margin-bottom:8px; }
    .ss-lb-list { display:flex; flex-direction:column; gap:6px; max-height:240px; overflow:auto;
      padding-right:4px; }
    .ss-lb-row { display:flex; align-items:center; gap:12px; padding:9px 14px; border-radius:0;
      background:var(--ov-row-even); border:2px solid transparent; }
    .ss-lb-row.odd { background:var(--ov-row-odd); }
    .ss-lb-row.top { background:var(--ov-top-row-bg); border-color:var(--ov-top-row-edge); }
    .ss-lb-rank { width:34px; color:var(--ov-label); font-weight:700; font-size:15px; }
    .ss-lb-row.top .ss-lb-rank { color:var(--ov-accent-gold); }
    .ss-lb-name { flex:1; color:#ffffff; font-weight:800; font-size:16px;
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .ss-lb-score { color:var(--ov-accent-score); font-weight:900; font-size:16px; }
    .ss-lb-row.top .ss-lb-score { color:var(--ov-accent-gold); }
    .ss-lb-empty { color:var(--ov-label); text-align:center; padding:14px; font-size:14px; font-weight:600; opacity:0.8; }
    .ss-lb-err { color:var(--ov-err); text-align:center; padding:14px; font-size:14px; font-weight:700; }
    `;
    const style = document.createElement('style');
    style.dataset.ss = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

const SAVE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4h11l3 3v13H5z"/><path d="M8 4v6h7V4"/><circle cx="12" cy="15" r="2.2" fill="currentColor" stroke="none"/></svg>';

export class ScoreSubmitOverlay {
    /** @type {HTMLDivElement|null} */
    _root = null;
    _destroyed = false;

    /**
     * @param {{
     *   game: string,
     *   score: number,
     *   outcome: 'win'|'game_over',
     *   meta?: object,
     *   theme?: import('../core/SkinResolver.js').SkinResolver,
     *   onDone: () => void,
     * }} opts
     */
    show(opts) {
        const { game, score, outcome, meta, theme, onDone } = opts;

        // Honour the global "Leaderboards" toggle. Two layers:
        //   1. Build-time `__BUILD_LEADERBOARDS__` — `false` means the
        //      bundle was compiled with leaderboards stripped, so the
        //      runtime check is moot but we still short-circuit here
        //      so any imported overlay code is provably dead.
        //   2. Runtime setting from OptionsScene's General panel.
        // When EITHER layer is off we don't save scores, don't ask
        // for a name, and don't render the high-score list — the
        // caller's `onDone` runs straight away so the existing Game
        // Over / Winner screens stay the player's final view.
        const lbBuildEnabled = typeof globalThis.__BUILD_LEADERBOARDS__ !== 'undefined'
            ? globalThis.__BUILD_LEADERBOARDS__
            : true;
        const lbEnabled = lbBuildEnabled
            && theme?.getSetting?.('shared', 'leaderboardsEnabled', true) !== false;
        if (!lbEnabled) {
            this._destroyed = true;
            // Async so callers that immediately mutate scene state
            // (e.g. tearing down the overlay that triggered us)
            // finish their own frame before we hand control back.
            queueMicrotask(() => { try { onDone?.(); } catch { /* swallow */ } });
            return;
        }

        injectCss();
        this._destroyed = false;

        const root = document.createElement('div');
        root.className = 'ss-root';
        this._root = root;

        const panel = document.createElement('div');
        panel.className = 'ss-panel';
        root.appendChild(panel);

        const titleRow = document.createElement('div');
        titleRow.className = 'ss-title-row';
        titleRow.innerHTML = `
            <span class="ss-star">★</span>
            <span class="ss-title">${escapeHtml(t('score.title'))}</span>
            <span class="ss-star">★</span>
        `;
        panel.appendChild(titleRow);

        const scoreCard = document.createElement('div');
        scoreCard.className = 'ss-card ss-score-card';
        scoreCard.innerHTML = `
            <div class="ss-score-label">${escapeHtml(t('common.score').toUpperCase())}</div>
            <div class="ss-score-val">${escapeHtml(String(score))}</div>
        `;
        panel.appendChild(scoreCard);

        const form = document.createElement('div');
        form.className = 'ss-form';
        panel.appendChild(form);

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 30;
        input.placeholder = t('score.name_placeholder');
        input.value = theme?.getLastPlayerName() || '';
        input.className = 'ss-input';
        form.appendChild(input);

        const btnRow = document.createElement('div');
        btnRow.className = 'ss-btn-row';
        form.appendChild(btnRow);

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'ss-btn ss-btn-save';
        saveBtn.innerHTML = `<span class="ss-btn-icon">${SAVE_ICON}</span>${escapeHtml(t('score.save'))}`;
        btnRow.appendChild(saveBtn);

        const skipBtn = document.createElement('button');
        skipBtn.type = 'button';
        skipBtn.className = 'ss-btn ss-btn-skip';
        skipBtn.textContent = t('score.skip');
        btnRow.appendChild(skipBtn);

        const statusEl = document.createElement('div');
        statusEl.className = 'ss-status';
        panel.appendChild(statusEl);

        const lbWrap = document.createElement('div');
        lbWrap.className = 'ss-card';
        panel.appendChild(lbWrap);

        const lbTitle = document.createElement('div');
        lbTitle.className = 'ss-lb-title';
        lbTitle.textContent = t('score.leaderboard');
        lbWrap.appendChild(lbTitle);

        const lbList = document.createElement('div');
        lbList.className = 'ss-lb-list';
        lbWrap.appendChild(lbList);

        this._loadLeaderboard(game, lbList);

        const setStatus = (msg, kind) => {
            statusEl.textContent = msg;
            statusEl.classList.remove('ss-status-ok', 'ss-status-err');
            if (kind === 'ok') statusEl.classList.add('ss-status-ok');
            if (kind === 'err') statusEl.classList.add('ss-status-err');
        };

        const doSave = async () => {
            const name = input.value.trim();
            if (!name) {
                input.focus();
                setStatus(t('score.name_required'), 'err');
                return;
            }
            saveBtn.disabled = true;
            setStatus('', null);
            try {
                if (theme) theme.setLastPlayerName(name);
                await ScoreService.submit({ game, player_name: name, score, outcome, meta });
                setStatus(t('score.saved'), 'ok');
                await this._loadLeaderboard(game, lbList);
                setTimeout(() => {
                    if (!this._destroyed) {
                        this.destroy();
                        onDone?.();
                    }
                }, 1200);
            } catch (err) {
                console.error('[ScoreSubmit]', err);
                setStatus(t('score.save_failed'), 'err');
                saveBtn.disabled = false;
            }
        };

        saveBtn.addEventListener('click', doSave);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') doSave();
        });
        skipBtn.addEventListener('click', () => {
            this.destroy();
            onDone?.();
        });

        document.body.appendChild(root);
        setTimeout(() => input.focus(), 80);
    }

    async _loadLeaderboard(game, container) {
        container.innerHTML = '';
        try {
            const rows = await ScoreService.getTop(game, 10);
            if (rows.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'ss-lb-empty';
                empty.textContent = t('score.no_scores');
                container.appendChild(empty);
                return;
            }
            rows.forEach((row, idx) => {
                const item = document.createElement('div');
                const cls = idx === 0 ? 'ss-lb-row top' : (idx % 2 === 1 ? 'ss-lb-row odd' : 'ss-lb-row');
                item.className = cls;

                const rank = document.createElement('span');
                rank.className = 'ss-lb-rank';
                rank.textContent = `${idx + 1}.`;
                item.appendChild(rank);

                const name = document.createElement('span');
                name.className = 'ss-lb-name';
                name.textContent = row.player_name;
                item.appendChild(name);

                const sc = document.createElement('span');
                sc.className = 'ss-lb-score';
                sc.textContent = String(row.score);
                item.appendChild(sc);

                container.appendChild(item);
            });
        } catch {
            const err = document.createElement('div');
            err.className = 'ss-lb-err';
            err.textContent = t('score.load_failed');
            container.appendChild(err);
        }
    }

    destroy() {
        this._destroyed = true;
        if (this._root?.parentNode) {
            this._root.parentNode.removeChild(this._root);
        }
        this._root = null;
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
